/**
 * What the scorer wrote into `qa_replay_run.criteria_evaluation`, in the two
 * shapes it has had.
 *
 * **Rubric v2** (BE `eval_scoring._payload`, be/RUBRIC.md R9.4.2) — criteria,
 * not axes:
 *
 *   { rubric_version: "v2", evaluation_policy,
 *     scores: {answer_quality, execution, final},        // 0–1
 *     groups: {common|category|case|execution: {weight, ratio, denominator,
 *              effective_weight, domain?, excluded?}},
 *     criteria: [{id, group, criteria_name, evaluator, verdict, weight,
 *                 effective_weight, contribution, note, measured?}],
 *     penalties: [{id, criteria_name, verdict, nominal, applied,
 *                  compensated_by, overlap_group, error_ref, note}],
 *     dropped_penalties: [{id, reason, kept, error_ref}],
 *     judge_model, judge_usage }
 *
 * **Rubric v1** (BE `eval_judge.judge_answer`) — three 1–5 axes:
 *
 *   { correctness: {score, reason}, answer_quality: {…}, clarity: {…},
 *     specific_findings: [{criterion, met, note}], rubric_version, judge_model }
 *
 * v1 is no longer written; its rows are read, not compared (R10). The two
 * are told apart by shape — v1 has a top-level `answer_quality` *object*,
 * v2 a `scores.answer_quality` *number*.
 *
 * Neither is the legacy `checks[]` shape Negative Reviews reads for a 👎
 * replay (`utils/replayEvaluation.ts`). Parsing stays lenient: a field that
 * is missing or of the wrong type reads as null, never as a guess.
 */

// --- v1 ----------------------------------------------------------------------

export type JudgeAxis = { key: string; title: string; score: number; reason: string };
export type JudgeFinding = { criterion: string; met: boolean; note: string };

export type JudgementV1 = {
  shape: "v1";
  axes: JudgeAxis[];
  findings: JudgeFinding[];
  rubricVersion: string | null;
  judgeModel: string | null;
};

// --- v2 ----------------------------------------------------------------------

/** R3.7. On a penalty, `met` means the problem **happened**. */
export type Verdict = "met" | "partial" | "unmet" | "not_applicable";

export type JudgeGroup = {
  key: string;
  title: string;
  weight: number | null;
  /** Share of the group's criteria met; null when every one was N/A. */
  ratio: number | null;
  denominator: number | null;
  /** The group's share of the final score after renormalising (R5). */
  effectiveWeight: number | null;
  /** Category only — null until `BE-DOMAIN`, which makes the group N/A. */
  domain: string | null;
  excluded: { id: string; reason: string }[];
};

export type JudgeCriterion = {
  id: string;
  group: string;
  name: string | null;
  evaluator: string | null;
  /** Null for a value outside the four — shown as it came, not coerced. */
  verdict: Verdict | null;
  rawVerdict: string;
  /** The criterion's weight inside its group (`score_impact`). */
  weight: number | null;
  effectiveWeight: number | null;
  /** `effective_weight × verdict value`; null for N/A. */
  contribution: number | null;
  note: string;
  /** Telemetry behind an execution verdict, e.g. `total_elapsed_to_message_ms`. */
  measured: Record<string, unknown> | null;
};

export type JudgePenalty = {
  id: string;
  name: string | null;
  verdict: Verdict | null;
  rawVerdict: string;
  nominal: number | null;
  applied: number | null;
  /** What the criteria it compensates had already cost (R6.1 상계). */
  compensatedBy: number | null;
  overlapGroup: string | null;
  errorRef: string;
  note: string;
};

export type DroppedPenalty = { id: string; reason: string; kept: string | null; errorRef: string };

export type JudgementV2 = {
  shape: "v2";
  rubricVersion: string | null;
  policy: string | null;
  scores: { answerQuality: number | null; execution: number | null; final: number | null };
  groups: JudgeGroup[];
  criteria: JudgeCriterion[];
  penalties: JudgePenalty[];
  dropped: DroppedPenalty[];
  judgeModel: string | null;
};

export type Judgement = JudgementV1 | JudgementV2;

// --- reading -----------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const num = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const str = (value: unknown): string => (typeof value === "string" ? value : "");
const strOrNull = (value: unknown): string | null => (typeof value === "string" && value ? value : null);
const records = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const VERDICTS: readonly Verdict[] = ["met", "partial", "unmet", "not_applicable"];
const verdictOf = (value: unknown): Verdict | null =>
  VERDICTS.includes(value as Verdict) ? (value as Verdict) : null;

/** R3.1's four groups, in the order the score is built — and the common rubric's `answer` (R16). */
const GROUP_TITLES: Record<string, string> = {
  common: "Common",
  category: "Category",
  case: "Case",
  answer: "Answer",
  execution: "Execution",
};
export const EXECUTION_GROUP = "execution";

/** Rubric v1's axis order and titles (BE `app/assets/eval/rubric_v1.json`). */
const KNOWN_AXES: Record<string, string> = {
  correctness: "Correctness",
  answer_quality: "Answer Quality",
  clarity: "Clarity",
};

function titleOf(key: string, known: Record<string, string>): string {
  return (
    known[key] ??
    key
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
  );
}

function orderBy<T>(items: T[], keyOf: (item: T) => string, order: string[]): T[] {
  const rank = (item: T) => {
    const i = order.indexOf(keyOf(item));
    return i < 0 ? order.length : i;
  };
  return [...items].sort((a, b) => rank(a) - rank(b));
}

function readV2(raw: Record<string, unknown>): JudgementV2 {
  const scores = isRecord(raw.scores) ? raw.scores : {};
  const groups = isRecord(raw.groups) ? raw.groups : {};
  return {
    shape: "v2",
    rubricVersion: strOrNull(raw.rubric_version),
    policy: strOrNull(raw.evaluation_policy),
    scores: {
      answerQuality: num(scores.answer_quality),
      execution: num(scores.execution),
      final: num(scores.final),
    },
    groups: orderBy(
      Object.entries(groups)
        .filter(([, value]) => isRecord(value))
        .map(([key, value]) => {
          const g = value as Record<string, unknown>;
          return {
            key,
            title: titleOf(key, GROUP_TITLES),
            weight: num(g.weight),
            ratio: num(g.ratio),
            denominator: num(g.denominator),
            effectiveWeight: num(g.effective_weight),
            domain: strOrNull(g.domain),
            excluded: records(g.excluded).map((e) => ({ id: str(e.id), reason: str(e.reason) })),
          };
        }),
      (g) => g.key,
      Object.keys(GROUP_TITLES),
    ),
    criteria: records(raw.criteria).map((c) => ({
      id: str(c.id),
      group: str(c.group),
      name: strOrNull(c.criteria_name),
      evaluator: strOrNull(c.evaluator),
      verdict: verdictOf(c.verdict),
      rawVerdict: str(c.verdict),
      weight: num(c.weight),
      effectiveWeight: num(c.effective_weight),
      contribution: num(c.contribution),
      note: str(c.note),
      measured: isRecord(c.measured) ? c.measured : null,
    })),
    penalties: records(raw.penalties).map((p) => ({
      id: str(p.id),
      name: strOrNull(p.criteria_name),
      verdict: verdictOf(p.verdict),
      rawVerdict: str(p.verdict),
      nominal: num(p.nominal),
      applied: num(p.applied),
      compensatedBy: num(p.compensated_by),
      overlapGroup: strOrNull(p.overlap_group),
      errorRef: str(p.error_ref),
      note: str(p.note),
    })),
    dropped: records(raw.dropped_penalties).map((d) => ({
      id: str(d.id),
      reason: str(d.reason),
      kept: strOrNull(d.kept),
      errorRef: str(d.error_ref),
    })),
    judgeModel: strOrNull(raw.judge_model),
  };
}

function readV1(raw: Record<string, unknown>): JudgementV1 | null {
  const axes: JudgeAxis[] = [];
  for (const [key, value] of Object.entries(raw)) {
    if (!isRecord(value) || typeof value.score !== "number") continue;
    axes.push({ key, title: titleOf(key, KNOWN_AXES), score: value.score, reason: str(value.reason) });
  }
  const findings: JudgeFinding[] = records(raw.specific_findings).map((f) => ({
    criterion: str(f.criterion),
    met: f.met === true,
    note: str(f.note),
  }));
  if (axes.length === 0 && findings.length === 0) return null;
  return {
    shape: "v1",
    axes: orderBy(axes, (a) => a.key, Object.keys(KNOWN_AXES)),
    findings,
    rubricVersion: strOrNull(raw.rubric_version),
    judgeModel: strOrNull(raw.judge_model),
  };
}

export function readJudgement(raw: unknown): Judgement | null {
  if (!isRecord(raw)) return null;
  if (isRecord(raw.scores) || raw.rubric_version === "v2") return readV2(raw);
  return readV1(raw);
}

// --- explaining the number ---------------------------------------------------

/**
 * How Final was built, from the stored parts (R3.1):
 * `clamp(aqWeight × AQ + exWeight × EX + Σ applied penalties, 0, 1)`.
 *
 * The weights come from the groups — Answer Quality's is every group but
 * Execution, whether or not one of them was N/A (the `/0.80` renormalising
 * happens inside AQ). `matches` is false when the recomputed number is not the
 * stored one — the weights can change and stored verdicts be re-summed (R10),
 * so the stored score is the one shown, and the mismatch is said.
 */
export function finalFormula(j: JudgementV2): {
  aqWeight: number;
  exWeight: number;
  penalty: number;
  computed: number;
  matches: boolean;
} | null {
  const { answerQuality, execution, final } = j.scores;
  if (answerQuality == null || execution == null) return null;
  const weightOf = (g: JudgeGroup) => g.weight ?? 0;
  const aqWeight = j.groups.filter((g) => g.key !== EXECUTION_GROUP).reduce((s, g) => s + weightOf(g), 0);
  const exWeight = weightOf(j.groups.find((g) => g.key === EXECUTION_GROUP) ?? ({ weight: 0 } as JudgeGroup));
  const penalty = j.penalties.reduce((s, p) => s + (p.applied ?? 0), 0);
  const computed = Math.min(1, Math.max(0, aqWeight * answerQuality + exWeight * execution + penalty));
  return { aqWeight, exWeight, penalty, computed, matches: final == null || Math.abs(computed - final) < 0.0005 };
}

/** What each verdict reads as — on a penalty, `met` is "it happened". */
export function verdictLabel(verdict: Verdict | null, raw: string, penalty = false): string {
  switch (verdict) {
    case "met":
      return penalty ? "발생" : "충족";
    case "partial":
      return penalty ? "일부 발생" : "일부 충족";
    case "unmet":
      return penalty ? "없음" : "미충족";
    case "not_applicable":
      return "N/A";
    default:
      return raw || "—";
  }
}

const MEASURED_LABEL: Record<string, (v: unknown) => string | null> = {
  total_elapsed_to_message_ms: (v) => (typeof v === "number" ? `응답 완료 ${(v / 1000).toFixed(1)}초` : null),
  tool_calls: (v) => (typeof v === "number" ? `도구 호출 ${v}` : null),
  failed: (v) => (typeof v === "number" ? `실패 ${v}` : null),
  answer_chars: (v) => (typeof v === "number" ? `답변 ${v}자` : null),
  error: (v) => (typeof v === "boolean" ? (v ? "오류 있음" : "오류 없음") : null),
};

/** An execution criterion's telemetry, as a person reads it. */
export function measuredText(measured: Record<string, unknown> | null): string {
  if (!measured) return "";
  return Object.entries(measured)
    .map(([key, value]) => MEASURED_LABEL[key]?.(value) ?? `${key} ${String(value)}`)
    .join(" · ");
}
